"use client";

/**
 * Client body for the Freshdesk integration page. Lives apart from `page.tsx`
 * (which stays a server component for `metadata`) so it can call `useT()` and
 * follow the visitor's locale, exactly like the homepage. All copy comes from
 * the `intgFreshdesk` namespace; structure (icons, hrefs, capability status)
 * stays here.
 */

import {
  Inbox,
  Reply,
  StickyNote,
  BookOpen,
  RefreshCw,
  History,
} from "lucide-react";
import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  UseCaseHero,
  Section,
  SectionHeader,
  FeatureGrid,
  CapabilityGrid,
  Faq,
  SecurityNote,
  RelatedLinks,
  CtaBanner,
} from "@/components/marketing/marketing-kit";
import { useT } from "@/i18n";

export function FreshdeskContent() {
  const t = useT();
  const k = (s: string) => `intgFreshdesk.${s}`;

  const features = [
    { key: "twoWaySync", icon: <RefreshCw size={18} /> },
    { key: "approvedReplies", icon: <Reply size={18} /> },
    { key: "internalNotes", icon: <StickyNote size={18} /> },
    { key: "knowledgeHarvest", icon: <BookOpen size={18} /> },
    { key: "supportContext", icon: <Inbox size={18} /> },
    { key: "evidenceAudit", icon: <History size={18} /> },
  ].map((f) => ({
    icon: f.icon,
    title: t(k(`features.${f.key}.title`)),
    body: t(k(`features.${f.key}.body`)),
  }));

  const capabilities = [
    { key: "readTickets", status: "supported" as const },
    { key: "publicReply", status: "conditional" as const },
    { key: "internalNote", status: "supported" as const },
    { key: "kbHarvest", status: "conditional" as const },
    { key: "twoWaySync", status: "conditional" as const },
    { key: "singleStatus", status: "unavailable" as const },
  ].map((c) => ({
    name: t(k(`capabilities.${c.key}.name`)),
    status: c.status,
    body: t(k(`capabilities.${c.key}.body`)),
  }));

  const faq = ["q1", "q2", "q3", "q4", "q5"].map((q) => ({
    q: t(k(`faq.${q}.q`)),
    a: t(k(`faq.${q}.a`)),
  }));

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("hero.eyebrow"))}
        title={t(k("hero.title"))}
        highlight={t(k("hero.highlight"))}
        subtitle={t(k("hero.subtitle"))}
        primary={{ href: "/demo", label: t(k("hero.primaryCta")) }}
        secondary={{ href: "/use-cases/freshdesk-jira-approved-replies", label: t(k("hero.secondaryCta")) }}
        bullets={[t(k("hero.bullet1")), t(k("hero.bullet2")), t(k("hero.bullet3"))]}
      />

      <Section>
        <SectionHeader
          eyebrow={t(k("what.eyebrow"))}
          title={t(k("what.title"))}
        />
        <FeatureGrid features={features} />
      </Section>

      <Section muted>
        <SectionHeader
          eyebrow={t(k("capabilities.eyebrow"))}
          title={t(k("capabilities.title"))}
          description={t(k("capabilities.description"))}
        />
        <CapabilityGrid
          capabilities={capabilities}
          statusLabels={{
            supported: t("mk.cap.supported"),
            conditional: t("mk.cap.conditional"),
            unavailable: t("mk.cap.unavailable"),
          }}
        />
      </Section>

      <Section>
        <SectionHeader eyebrow={t(k("governed.eyebrow"))} title={t(k("governed.title"))} />
        <SecurityNote icon={<History size={18} />} title={t(k("governed.noteTitle"))}>
          {t(k("governed.noteBody"))}
        </SecurityNote>
      </Section>

      <Section muted>
        <SectionHeader eyebrow={t(k("faq.eyebrow"))} title={t(k("faq.title"))} />
        <Faq items={faq} />
      </Section>

      <Section>
        <RelatedLinks
          title={t("mk.relatedTitle")}
          links={[
            {
              href: "/use-cases/freshdesk-jira-approved-replies",
              label: t(k("related.approvedReplies.label")),
              description: t(k("related.approvedReplies.description")),
            },
            {
              href: "/use-cases/support-to-engineering-remediation",
              label: t(k("related.supportToEng.label")),
              description: t(k("related.supportToEng.description")),
            },
            {
              href: "/integrations/jira",
              label: t(k("related.jira.label")),
              description: t(k("related.jira.description")),
            },
          ]}
        />
      </Section>

      <CtaBanner
        title={t(k("cta.title"))}
        description={t(k("cta.description"))}
        primary={{ href: "/demo", label: t(k("cta.primary")) }}
        secondary={{ href: "/contact", label: t(k("cta.secondary")) }}
      />
    </MarketingShell>
  );
}
